import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Alert } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import { MessageCircle, Send, Smile, Paperclip, X, RefreshCw, User, Clock, CheckCircle, XCircle } from 'lucide-react';
import { io } from 'socket.io-client';
import api from '../services/api';
import { useNotifications } from '../context/NotificationContext';

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 }
};

const QUICK_EMOJIS = ['😊', '👍', '🙏', '✅', '❤️', '🎉', '👋', '😄', '🤝', '💡', '📧', '🔑', '⏳', '✨', '🚀', '😢', '😮', '🤔', '👀', '🙌'];

function SupportChats() {
  const { refresh: refreshNotifications } = useNotifications();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [activeConvo, setActiveConvo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [chatUser, setChatUser] = useState(null);
  const [botUserId, setBotUserId] = useState(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [closing, setClosing] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [typingUser, setTypingUser] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const inputRef = useRef(null);
  const socketRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const isTypingRef = useRef(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const baseURL = (process.env.REACT_APP_API_URL || 'http://localhost:3001/api').replace(/\/api$/, '');
    const s = io(baseURL, { auth: { token }, transports: ['websocket', 'polling'] });
    socketRef.current = s;

    s.on('typing:start', ({ conversationId, name }) => {
      if (conversationId === activeConvoRef.current) {
        setTypingUser(name || 'User');
      }
    });
    s.on('typing:stop', ({ conversationId }) => {
      if (conversationId === activeConvoRef.current) {
        setTypingUser(null);
      }
    });
    s.on('message:new', (msg) => {
      if (msg.conversation_id === activeConvoRef.current) {
        setMessages(prev => {
          if (prev.find(m => m.id === msg.id)) return prev;
          return [...prev, msg];
        });
      }
    });

    return () => { s.disconnect(); };
  }, []);

  const activeConvoRef = useRef(activeConvo);
  useEffect(() => { activeConvoRef.current = activeConvo; }, [activeConvo]);

  useEffect(() => { loadConversations(); }, []);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const loadConversations = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const r = await api.get('/admin/support-chats');
      setConversations(r.data.conversations || []);
      setBotUserId(r.data.botUserId);
      setError('');
    } catch (e) {
      setError('Failed to load support conversations');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const emitTyping = useCallback(() => {
    if (!activeConvoRef.current || !socketRef.current) return;
    if (!isTypingRef.current) {
      isTypingRef.current = true;
      socketRef.current.emit('typing:start', { conversationId: activeConvoRef.current, displayName: 'Resunize Support' });
    }
    clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      isTypingRef.current = false;
      socketRef.current?.emit('typing:stop', { conversationId: activeConvoRef.current });
    }, 2000);
  }, []);

  const stopTyping = useCallback(() => {
    if (isTypingRef.current && activeConvoRef.current && socketRef.current) {
      isTypingRef.current = false;
      socketRef.current.emit('typing:stop', { conversationId: activeConvoRef.current });
    }
    clearTimeout(typingTimeoutRef.current);
  }, []);

  const openConversation = async (convoId) => {
    stopTyping();
    setTypingUser(null);
    if (activeConvo) socketRef.current?.emit('leave:conversation', activeConvo);
    setActiveConvo(convoId);
    socketRef.current?.emit('join:conversation', convoId);
    setMessagesLoading(true);
    setShowEmoji(false);
    try {
      const r = await api.get(`/admin/support-chats/${convoId}`);
      setMessages(r.data.messages || []);
      setChatUser(r.data.user);
      setBotUserId(r.data.botUserId);
    } catch (e) {
      toast.error('Failed to load messages');
    } finally {
      setMessagesLoading(false);
    }
  };

  const activeConvoData = conversations.find(c => c.id === activeConvo);
  const isClosed = !!activeConvoData?.closed_at;

  const endChat = async () => {
    if (!activeConvo) return;
    if (!window.confirm('End this support conversation? A closing message will be sent to the user.')) return;
    setClosing(true);
    try {
      const r = await api.post(`/admin/support-chats/${activeConvo}/close`);
      setMessages(prev => [...prev, r.data.message]);
      await loadConversations(true);
      refreshNotifications();
      toast.success('Conversation closed');
    } catch (e) {
      toast.error('Failed to close conversation');
    } finally {
      setClosing(false);
    }
  };

  const sendReply = async () => {
    if (!reply.trim() || !activeConvo) return;
    stopTyping();
    setSending(true);
    try {
      const r = await api.post(`/admin/support-chats/${activeConvo}/reply`, { body: reply.trim() });
      setMessages(prev => [...prev, r.data.message]);
      setReply('');
      setShowEmoji(false);
      loadConversations(true);
      refreshNotifications();
      setTimeout(() => inputRef.current?.focus(), 50);
    } catch (e) {
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeConvo) return;
    setSelectedFile(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const addEmoji = (emoji) => {
    setReply(prev => prev + emoji);
    inputRef.current?.focus();
  };

  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(dateStr).toLocaleDateString();
  };

  const roleBadge = (role) => {
    const styles = {
      shadchan: { bg: 'var(--info-light)', color: 'var(--info)' },
      parent: { bg: 'var(--warning-light)', color: 'var(--warning)' },
      single: { bg: 'var(--success-light)', color: 'var(--success)' }
    };
    const s = styles[role] || { bg: 'var(--surface-hover)', color: 'var(--text-secondary)' };
    return (
      <span style={{
        display: 'inline-block', padding: '0.125rem 0.5rem', borderRadius: 'var(--radius-full)',
        fontSize: '0.7rem', fontWeight: 600, background: s.bg, color: s.color, textTransform: 'capitalize'
      }}>{role}</span>
    );
  };

  const filteredConversations = searchQuery.trim()
    ? conversations.filter(c => {
        const hay = `${c.first_name} ${c.last_name} ${c.email} ${c.role}`.toLowerCase();
        return hay.includes(searchQuery.trim().toLowerCase());
      })
    : conversations;

  if (loading) {
    return (
      <div className="page-loading">
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={{ duration: 0.4 }}>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
        <div>
          <h1 className="page-title">Support Chats</h1>
          <p className="page-subtitle">View and respond to user support conversations</p>
        </div>
        <button
          className="btn btn-outline-primary btn-sm"
          onClick={() => loadConversations(true)}
          disabled={refreshing}
          style={{ borderRadius: 'var(--radius-lg)', padding: '0.5rem 1rem' }}
        >
          <RefreshCw size={14} className={refreshing ? 'spin' : ''} style={{ marginRight: '0.375rem' }} />
          Refresh
        </button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <div style={{ display: 'flex', gap: '1.25rem', height: 'calc(100vh - 200px)', minHeight: '500px' }}>
        {/* Left panel - Conversation list */}
        <div className="glass-card" style={{ width: '380px', minWidth: '320px', padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', borderRadius: 'var(--radius-xl)' }}>
          {/* Search */}
          <div style={{ padding: '0.875rem 1rem', borderBottom: '1px solid var(--border-light)' }}>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search conversations..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                style={{ borderRadius: 'var(--radius-full)', paddingLeft: '2.25rem', fontSize: '0.85rem' }}
              />
              <MessageCircle size={14} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
            </div>
            <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              {filteredConversations.length} conversation{filteredConversations.length !== 1 ? 's' : ''}
            </div>
          </div>

          {/* List */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {filteredConversations.length === 0 ? (
              <div style={{ padding: '3rem 1.5rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                <MessageCircle size={40} style={{ opacity: 0.3, marginBottom: '0.75rem' }} />
                <div style={{ fontSize: '0.9rem' }}>{searchQuery ? 'No matching conversations' : 'No support conversations yet'}</div>
              </div>
            ) : (
              filteredConversations.map(c => {
                const isActive = activeConvo === c.id;
                return (
                  <motion.div
                    key={c.id}
                    onClick={() => openConversation(c.id)}
                    whileHover={{ backgroundColor: isActive ? undefined : 'var(--surface-hover)' }}
                    style={{
                      padding: '0.875rem 1rem',
                      borderBottom: '1px solid var(--border-light)',
                      cursor: 'pointer',
                      background: isActive ? 'var(--accent-light)' : 'transparent',
                      borderLeft: isActive ? '3px solid var(--accent)' : '3px solid transparent',
                      transition: 'all 0.15s'
                    }}
                  >
                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'start' }}>
                      <div style={{
                        width: 40, height: 40, borderRadius: 'var(--radius-full)', flexShrink: 0,
                        background: isActive ? 'var(--accent)' : 'var(--surface-solid)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: isActive ? '#fff' : 'var(--text-secondary)', fontWeight: 700, fontSize: '0.85rem'
                      }}>
                        {(c.first_name || '?')[0]}{(c.last_name || '?')[0]}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.875rem' }}>
                            {c.first_name} {c.last_name}
                          </div>
                          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', flexShrink: 0 }}>
                            {timeAgo(c.last_message_at)}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginTop: '0.125rem', flexWrap: 'wrap' }}>
                          {roleBadge(c.role)}
                          {c.closed_at && (
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: '0.2rem',
                              padding: '0.1rem 0.5rem', borderRadius: 'var(--radius-full)',
                              fontSize: '0.7rem', fontWeight: 600, background: 'var(--danger-light)', color: 'var(--danger)'
                            }}>
                              <CheckCircle size={10} /> Closed
                            </span>
                          )}
                          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{c.email}</span>
                        </div>
                        {c.last_message && (
                          <div style={{
                            fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.375rem',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                          }}>
                            {c.last_message.slice(0, 70)}{c.last_message.length > 70 ? '…' : ''}
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })
            )}
          </div>
        </div>

        {/* Right panel - Chat area */}
        <div className="glass-card" style={{ flex: 1, padding: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: 'var(--radius-xl)' }}>
          {!activeConvo ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
                <MessageCircle size={56} style={{ opacity: 0.15, marginBottom: '1rem' }} />
                <div style={{ fontSize: '1.1rem', fontWeight: 500 }}>Select a conversation</div>
                <div style={{ fontSize: '0.85rem', marginTop: '0.375rem' }}>Choose a support conversation from the list to view and respond</div>
              </div>
            </div>
          ) : (
            <>
              {/* Header */}
              {chatUser && (
                <div style={{
                  padding: '1rem 1.25rem', borderBottom: '1px solid var(--border-light)',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  background: 'var(--surface)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem' }}>
                    <div style={{
                      width: 44, height: 44, borderRadius: 'var(--radius-full)',
                      background: 'linear-gradient(135deg, var(--accent), var(--accent-hover))',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#fff', fontWeight: 700, fontSize: '0.95rem',
                      boxShadow: '0 2px 8px rgba(123, 47, 247, 0.3)'
                    }}>
                      {(chatUser.first_name || '?')[0]}{(chatUser.last_name || '?')[0]}
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.95rem' }}>
                        {chatUser.first_name} {chatUser.last_name}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.125rem' }}>
                        <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{chatUser.email}</span>
                        {roleBadge(chatUser.role)}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                      <Clock size={12} style={{ marginRight: '0.25rem' }} />
                      {messages.length} messages
                    </span>
                    {isClosed ? (
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
                        padding: '0.375rem 0.75rem', borderRadius: 'var(--radius-full)',
                        fontSize: '0.8rem', fontWeight: 600, background: 'var(--danger-light)', color: 'var(--danger)'
                      }}>
                        <CheckCircle size={14} /> Closed
                      </span>
                    ) : (
                      <button
                        onClick={endChat}
                        disabled={closing}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
                          padding: '0.375rem 0.875rem', borderRadius: 'var(--radius-full)',
                          fontSize: '0.8rem', fontWeight: 600, border: '1px solid var(--danger)',
                          background: 'transparent', color: 'var(--danger)', cursor: 'pointer',
                          transition: 'all 0.15s'
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'var(--danger)'; e.currentTarget.style.color = '#fff'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--danger)'; }}
                      >
                        <XCircle size={14} />
                        {closing ? 'Closing...' : 'End Chat'}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Messages */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '1.25rem', background: 'var(--bg-subtle)' }}>
                {messagesLoading ? (
                  <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                    <div className="loading-spinner" style={{ width: 28, height: 28, margin: '0 auto 0.75rem' }} />
                    <div style={{ fontSize: '0.85rem' }}>Loading messages...</div>
                  </div>
                ) : (
                  <>
                    {messages.map((m, i) => {
                      const isBot = m.sender_id === botUserId;
                      const showDate = i === 0 || new Date(m.created_at).toDateString() !== new Date(messages[i - 1].created_at).toDateString();
                      return (
                        <React.Fragment key={m.id}>
                          {showDate && (
                            <div style={{ textAlign: 'center', margin: '1rem 0', position: 'relative' }}>
                              <span style={{
                                background: 'var(--bg-subtle)', padding: '0.25rem 0.875rem',
                                fontSize: '0.75rem', color: 'var(--text-muted)', borderRadius: 'var(--radius-full)',
                                border: '1px solid var(--border-light)'
                              }}>
                                {new Date(m.created_at).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                              </span>
                            </div>
                          )}
                          <div style={{ display: 'flex', justifyContent: isBot ? 'flex-end' : 'flex-start', marginBottom: '0.625rem' }}>
                            {!isBot && (
                              <div style={{
                                width: 30, height: 30, borderRadius: 'var(--radius-full)', marginRight: '0.5rem',
                                background: 'var(--surface-solid)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                flexShrink: 0, marginTop: '0.125rem'
                              }}>
                                <User size={14} style={{ color: 'var(--text-muted)' }} />
                              </div>
                            )}
                            <div style={{
                              maxWidth: '65%', padding: '0.75rem 1rem',
                              borderRadius: isBot ? '1rem 1rem 0.25rem 1rem' : '1rem 1rem 1rem 0.25rem',
                              background: isBot ? 'linear-gradient(135deg, var(--accent), var(--accent-hover))' : 'var(--surface-solid)',
                              color: isBot ? '#fff' : 'var(--text-primary)',
                              fontSize: '0.875rem', lineHeight: 1.55,
                              boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
                            }}>
                              <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.body}</div>
                              <div style={{
                                fontSize: '0.675rem', marginTop: '0.375rem',
                                opacity: 0.65, textAlign: 'right',
                                display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.375rem'
                              }}>
                                {isBot && <span>🤖</span>}
                                <span>{new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                              </div>
                            </div>
                            {isBot && (
                              <div style={{
                                width: 30, height: 30, borderRadius: 'var(--radius-full)', marginLeft: '0.5rem',
                                background: 'linear-gradient(135deg, var(--accent), var(--accent-hover))',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                flexShrink: 0, marginTop: '0.125rem', fontSize: '0.8rem'
                              }}>
                                🤖
                              </div>
                            )}
                          </div>
                        </React.Fragment>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </>
                )}
                {typingUser && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 16px 8px', color: '#888', fontSize: '0.82rem' }}>
                    <span className="admin-typing-dots"><span></span><span></span><span></span></span>
                    <span>{typingUser} is typing...</span>
                  </div>
                )}
              </div>

              {/* Emoji picker */}
              <AnimatePresence>
                {showEmoji && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    style={{ overflow: 'hidden', borderTop: '1px solid var(--border-light)' }}
                  >
                    <div style={{ padding: '0.625rem 1rem', display: 'flex', flexWrap: 'wrap', gap: '0.25rem', background: 'var(--surface)' }}>
                      {QUICK_EMOJIS.map(e => (
                        <button
                          key={e}
                          onClick={() => addEmoji(e)}
                          style={{
                            border: 'none', background: 'transparent', fontSize: '1.35rem', cursor: 'pointer',
                            padding: '0.25rem 0.375rem', borderRadius: 'var(--radius-sm)',
                            transition: 'transform 0.1s, background 0.1s'
                          }}
                          onMouseEnter={ev => { ev.currentTarget.style.transform = 'scale(1.25)'; ev.currentTarget.style.background = 'var(--surface-hover)'; }}
                          onMouseLeave={ev => { ev.currentTarget.style.transform = 'scale(1)'; ev.currentTarget.style.background = 'transparent'; }}
                        >
                          {e}
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* File preview */}
              {selectedFile && (
                <div style={{ padding: '0.5rem 1rem', borderTop: '1px solid var(--border-light)', display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'var(--surface)' }}>
                  <Paperclip size={14} style={{ color: 'var(--accent)' }} />
                  <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {selectedFile.name}
                  </span>
                  <button onClick={removeFile} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: '0.25rem', color: 'var(--text-muted)' }}>
                    <X size={14} />
                  </button>
                </div>
              )}

              {/* Reply bar */}
              {isClosed ? (
                <div style={{
                  padding: '1rem', borderTop: '1px solid var(--border-light)',
                  textAlign: 'center', background: 'var(--surface)', color: 'var(--text-muted)', fontSize: '0.85rem'
                }}>
                  <CheckCircle size={16} style={{ marginRight: '0.375rem', verticalAlign: 'middle' }} />
                  This conversation has been closed. The user can start a new one if needed.
                </div>
              ) : (
              <div style={{
                padding: '0.75rem 1rem', borderTop: '1px solid var(--border-light)',
                display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'var(--surface)'
              }}>
                <button
                  onClick={() => setShowEmoji(prev => !prev)}
                  style={{
                    border: 'none', background: showEmoji ? 'var(--accent-light)' : 'transparent',
                    cursor: 'pointer', padding: '0.5rem', borderRadius: 'var(--radius-full)',
                    color: showEmoji ? 'var(--accent)' : 'var(--text-muted)', transition: 'all 0.15s',
                    display: 'flex', alignItems: 'center'
                  }}
                  title="Emoji"
                >
                  <Smile size={20} />
                </button>

                <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileSelect} />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    border: 'none', background: 'transparent', cursor: 'pointer', padding: '0.5rem',
                    borderRadius: 'var(--radius-full)', color: 'var(--text-muted)',
                    display: 'flex', alignItems: 'center', transition: 'color 0.15s'
                  }}
                  title="Attach file"
                >
                  <Paperclip size={20} />
                </button>

                <input
                  ref={inputRef}
                  type="text"
                  className="form-control"
                  placeholder="Type a reply as Resunize Support..."
                  value={reply}
                  onChange={e => { setReply(e.target.value); emitTyping(); }}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                  disabled={sending}
                  style={{ borderRadius: 'var(--radius-full)', fontSize: '0.875rem', flex: 1 }}
                />

                <button
                  onClick={sendReply}
                  disabled={sending || !reply.trim()}
                  style={{
                    border: 'none', cursor: reply.trim() ? 'pointer' : 'default',
                    background: reply.trim() ? 'linear-gradient(135deg, var(--accent), var(--accent-hover))' : 'var(--surface-solid)',
                    color: reply.trim() ? '#fff' : 'var(--text-muted)',
                    padding: '0.5rem', borderRadius: 'var(--radius-full)',
                    display: 'flex', alignItems: 'center', transition: 'all 0.2s',
                    boxShadow: reply.trim() ? '0 2px 8px rgba(123, 47, 247, 0.3)' : 'none'
                  }}
                  title="Send"
                >
                  <Send size={20} />
                </button>
              </div>
              )}
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .spin { animation: spin 0.8s linear infinite; }
      `}</style>
    </motion.div>
  );
}

export default SupportChats;
